import React, { useState, useContext, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { AuthContext } from "../../shared/context/auth-context";

async function checkDriveUrl(driveId) {
	const url = `https://drive.google.com/file/d/${driveId}/preview`;
	try {
		const response = await fetch(url, {
			method: "HEAD",
		});
		return response.ok;
	} catch (err) {
		console.error("Drive check failed:", err);
		return false;
	}
}

export default function TicketAction({
	ticketId,
	status,
	adminView,
	producerView,
	permissions,
	onActionComplete,
	assigneeId,
	videoId,
	watermarkVideoId,
	setVideoId,
	setWatermarkVideoId,
	deposit,
	additionalFee,
	setAdditionalFee,
	waivePayment,
	setWaivePayment,
	finalIterationFlag,
	waived,
}) {
	const { sendRequest } = useHttpClient();
	const authContext = useContext(AuthContext);

	// Example state for iteration or approvals
	const [producerId, setProducerId] = useState(""); // if admin needs to assign a specific producer
	const [producers, setProducers] = useState([]);
	const [finalIteration, setFinalIteration] = useState(false); // for admin final iteration approval
	// Determine what action label, which endpoint, and what fields to show
	let actionLabel = null;
	let showVideoInput = false;
	let showApprovalRadio = false;
	let showProducerSelect = false;
	let showFinalIterationOption = false;
	let showAdminCompleteOption = false;
	let endpoint = null;
	let body = {};

	// Decide user role
	/*const isAdmin = adminView; // || authContext?.isSuperAdmin;
	const isProducer = producerView && !isAdmin;
	const isUser = !isAdmin && !isProducer;*/

	const userPermissions = permissions || {
		hasAdminPrivileges: adminView,
		hasProducerPrivileges: producerView,
		hasUserPrivileges: !adminView && !producerView,
	};

	// Fetch producers if needed

	useEffect(() => {
		const fetchProducers = async () => {
			try {
				let producerResponse = [];

				const response = await sendRequest(
					"/api/users/producers",
					"GET",
					null,
					{
						Authorization: "Bearer " + authContext.token,
					}
				);
				producerResponse = response.data;
				console.log("Producers:", producerResponse);
				setProducers(producerResponse);
			} catch (err) {
				console.error("Error fetching producers:", err);
			}
		};

		if (status === "open" && userPermissions.hasAdminPrivileges) fetchProducers();
	}, [userPermissions.hasAdminPrivileges, status, sendRequest, authContext.token]);

	// Based on status + role, set logic
	if (status === "open" && userPermissions.hasAdminPrivileges) {
		actionLabel = "Assign Producer";
		endpoint = `/api/tickets/${ticketId}/assign`;
		showProducerSelect = true; // example: choose which producer to assign
	} else if (status === "admin review" && userPermissions.hasAdminPrivileges) {
		actionLabel = "Submit Admin Review";
		endpoint = `/api/tickets/${ticketId}/admin-approve`;
		showApprovalRadio = true;
		showFinalIterationOption = true;
	} else if (status === "in progress" && userPermissions.hasProducerPrivileges) {
		actionLabel = "Submit Iteration";
		endpoint = `/api/tickets/${ticketId}/update`;
		showVideoInput = true; // gather new videoId
	} else if (status === "in review" && userPermissions.hasUserPrivileges) {
		actionLabel = finalIterationFlag ? "Final Approval" : "User Approval";
		endpoint = `/api/tickets/${ticketId}/user-approve`;
		showApprovalRadio = true;
	} else if (status === "in review" && userPermissions.hasAdminPrivileges && waived) {
		// Admin can complete waived tickets directly
		actionLabel = "Complete Waived Ticket";
		endpoint = `/api/tickets/${ticketId}/admin-complete`;
		showAdminCompleteOption = true;
	} else {
		// If the ticket is in a final state or user has no action, we can hide the button
		return null;
	}

	// Handle button click
	const handleAction = async (approveValue) => {
		try {
			// Build request body based on which action
			if (endpoint.includes("/assign")) {
				// /tickets/:ticketId/assign => { producerId }
				body = {
					producerId,
					waivePayment: waivePayment || false,
				};
				/*} else if (endpoint.includes("/accept")) {
				// /tickets/:ticketId/accept => no body needed
				body = {};*/
			} else if (endpoint.includes("/admin-approve")) {
				// /tickets/:ticketId/admin-approve => { approved }
				body = {
					approved: approveValue,
					//optional additional fee
					additionalFee: additionalFee ? parseFloat(additionalFee) : 0,
					//final iteration flag
					finalIteration: approveValue && finalIteration,
				};
				console.log("Admin approval body:", body);
			} else if (endpoint.includes("/update")) {
				// /tickets/:ticketId/update => { videoId }
				const isValid = await checkDriveUrl(videoId);
				if (isValid) {
					body = {
						videoId,
						watermarkVideoId,
					};
				} else {
					alert(
						"Invalid video ID. Please check the ID in the drive URL and try again."
					);
					return;
				}
			} else if (endpoint.includes("/user-approve")) {
				// /tickets/:ticketId/user-approve => { approved }
				body = { approved: approveValue };
			} else if (endpoint.includes("/admin-complete")) {
				// /tickets/:ticketId/admin-complete => no body needed
				body = {};
			}

			const response = await sendRequest(
				endpoint,
				"POST",
				JSON.stringify(body),
				{
					"Content-Type": "application/json",
					Authorization: `Bearer ${authContext.token}`,
				}
			);

			console.log("Action response:", response.data);
			if (onActionComplete) onActionComplete();
		} catch (err) {
			console.error("Error with ticket action:", err);
		}
	};

	const handleVideoBlur = async (e, fieldType = "regular") => {
		const typedId = e.target.value.trim();

		if (!typedId) {
			// Clear the appropriate field based on type
			if (fieldType === "watermark") {
				setWatermarkVideoId("");
			} else {
				setVideoId("");
			}
			return;
		}

		const isValid = await checkDriveUrl(typedId);
		if (isValid) {
			// Set the appropriate field based on type
			if (fieldType === "watermark") {
				setWatermarkVideoId(typedId);
			} else {
				setVideoId(typedId);
			}
		} else {
			const fieldLabel =
				fieldType === "watermark" ? "watermark video" : "video";
			alert(
				`Invalid ${fieldLabel} ID. Please check the ID in the drive URL and try again.`
			);

			// Clear invalid value from appropriate field
			if (fieldType === "watermark") {
				setWatermarkVideoId("");
			} else {
				setVideoId("");
			}
		}
	};

	return (
		<div style={{ marginTop: "1rem" }}>
			{/* Producer select dropdown if needed */}
			{showProducerSelect && (
				<React.Fragment>
					<Form.Group className="mb-3" controlId="ticketOrgId">
						<Form.Label>Producer</Form.Label>
						<Form.Control
							as="select"
							value={producerId}
							onChange={(e) => setProducerId(e.target.value)}
							required
						>
							<option value="">Assign to a producer</option>
							{producers.map((producer) => (
								<option key={producer.id} value={producer.id}>
									{producer.firstName} {producer.lastName} {producer.id}
								</option>
							))}
						</Form.Control>
						<Form.Check
							type="checkbox"
							label="Waive Payment"
							checked={waivePayment}
							onChange={(e) => setWaivePayment(e.target.checked)}
						/>
					</Form.Group>
				</React.Fragment>
			)}

			{/* Video input if iteration needed */}
			{showVideoInput && (
				<React.Fragment>
					<Form.Group>
						<Form.Label>New Video ID (WATERMARKED)</Form.Label>
						<Form.Control
							type="text"
							value={watermarkVideoId}
							placeholder="Enter WATERMARKED VIDEO ID"
							onChange={(e) => setWatermarkVideoId(e.target.value)}
							onBlur={(e) => handleVideoBlur(e, "watermark")}
							disabled={!deposit}
						/>
					</Form.Group>
					<Form.Group>
						<Form.Label>New Video ID</Form.Label>
						<Form.Control
							type="text"
							value={videoId}
							placeholder="Enter new Video ID"
							onChange={(e) => setVideoId(e.target.value)}
							onBlur={(e) => handleVideoBlur(e, "regular")}
							disabled={!deposit}
						/>
					</Form.Group>
				</React.Fragment>
			)}

			{/* Approval radio for admin/user approve */}
			{showApprovalRadio ? (
				//switch radio to two buttons
				<div>
					{finalIterationFlag && status === "in review" && userPermissions.hasUserPrivileges ? (
						// Final iteration - user can only approve
						<>
							<div className="alert alert-warning mb-3">
								<strong>Final Iteration:</strong> This is the final version. You can only approve and proceed to payment.
							</div>
							<Button
								variant="success"
								className="mt-2"
								onClick={() => {
									handleAction(true);
								}}
							>
								Approve Final Version and Pay
							</Button>
						</>
					) : (
						// Normal flow - user can approve or reject
						<>
							<Button
								variant="primary"
								className="mt-2"
								//set approved to true and call handleAction
								onClick={() => {
									handleAction(true);
								}}
							>
								Approve Changes {status === "in review" && " and Pay"}
							</Button>
							<span> OR </span>
							<Button
								variant="secondary"
								className="mt-2"
								onClick={() => {
									handleAction(false);
								}}
							>
								Request Changes
							</Button>
						</>
					)}
					{userPermissions.hasAdminPrivileges && (
						<>
							<Form.Group>
								<Form.Label>(Optional) Additional Fees</Form.Label>
								<Form.Control
									type="number"
									min="0"
									step="1"
									value={additionalFee}
									placeholder="0.00"
									prefix="$"
									onChange={(e) => setAdditionalFee(e.target.value)}
									inputMode="decimal"
								/>
							</Form.Group>
							{showFinalIterationOption && (
								<Form.Group className="mt-3">
									<Form.Check
										type="checkbox"
										label="Mark as Final Iteration (User cannot reject - only approve and pay)"
										checked={finalIteration}
										onChange={(e) => setFinalIteration(e.target.checked)}
									/>
									<Form.Text className="text-muted">
										When checked, the user will only be able to approve and proceed to payment. They cannot request further changes.
									</Form.Text>
								</Form.Group>
							)}
						</>
					)}
				</div>
			) : showAdminCompleteOption ? (
				// Admin complete for waived tickets
				<div>
					<div className="alert alert-info mb-3">
						<strong>Waived Ticket:</strong> Payment was waived for this ticket. You can complete it directly without user review.
					</div>
					<Button
						variant="success"
						className="mt-2"
						onClick={handleAction}
					>
						{actionLabel}
					</Button>
				</div>
			) : (
				<Button
					variant="primary"
					className="mt-2"
					onClick={handleAction}
					disabled={!actionLabel} // or check if required fields are set
				>
					{actionLabel}
				</Button>
			)}
		</div>
	);
}
